import { useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { branding, orbitPlatform } from '../config/brandingConfig'
import { clearOrbitRuntimeCaches } from '../services/cacheService'
import {
  buildLoginRedirectRequest,
  clearAuthError,
  getAuthError,
} from '../services/authSession'

/* ------------------------------------------------------------------ */
/*  Large immersive orbit — used as visual backdrop                    */
/* ------------------------------------------------------------------ */
function OrbitBackdrop() {
  const orbitPath =
    'M 164.5,-95 A 190,72 -30 0 1 -164.5,95 A 190,72 -30 0 1 164.5,-95'

  return (
    <svg
      viewBox="0 0 512 512"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="login-orbit-backdrop"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="lr" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a5b4fc" />
          <stop offset="40%" stopColor="#818cf8" />
          <stop offset="70%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#4338ca" />
        </linearGradient>
        <radialGradient id="cr" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="35%" stopColor="#e0e7ff" />
          <stop offset="70%" stopColor="#a5b4fc" />
          <stop offset="100%" stopColor="#6366f1" />
        </radialGradient>
        <radialGradient id="nr" cx="40%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="40%" stopColor="#c7d2fe" />
          <stop offset="100%" stopColor="#818cf8" />
        </radialGradient>
        <filter id="gl" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="10" />
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      <circle cx="256" cy="256" r="210" stroke="#818cf8" strokeWidth="0.5" opacity="0.12" />
      <circle cx="256" cy="256" r="170" stroke="#818cf8" strokeWidth="0.3" opacity="0.08" />

      <ellipse
        cx="256" cy="256" rx="190" ry="72"
        stroke="url(#lr)" strokeWidth="14" strokeLinecap="round"
        fill="none" transform="rotate(-30 256 256)" opacity="0.95"
      />

      <circle cx="256" cy="256" r="34" fill="url(#cr)" filter="url(#gl)" />
      <circle cx="256" cy="256" r="16" fill="white" opacity="0.55" />

      <g transform="translate(256,256)">
        <g>
          <animateMotion dur="25s" repeatCount="indefinite" rotate="auto" path={orbitPath} />
          <circle cx="-18" cy="0" r="20" fill="#818cf8" opacity="0.10" />
          <circle cx="-36" cy="0" r="5" fill="#c7d2fe" opacity="0.40" />
          <circle cx="-58" cy="0" r="3" fill="#c7d2fe" opacity="0.20" />
          <circle cx="0" cy="0" r="24" fill="url(#nr)" filter="url(#gl)" className="login-satellite-pulse" />
          <circle cx="0" cy="0" r="11" fill="white" opacity="0.45" />
        </g>
      </g>
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/*  Microsoft coloured logo                                           */
/* ------------------------------------------------------------------ */
function MicrosoftLogo({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 21 21" fill="none">
      <rect x="0" y="0" width="10" height="10" fill="#f25022" />
      <rect x="11" y="0" width="10" height="10" fill="#7fba00" />
      <rect x="0" y="11" width="10" height="10" fill="#00a4ef" />
      <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
    </svg>
  )
}

export default function LoginPage() {
  const { instance, inProgress } = useMsal()
  const buildTag = __APP_BUILD_ID__
  const [loginError, setLoginError] = useState<string | null>(null)
  const [isResetting, setIsResetting] = useState(false)
  const [isCheckingSession, setIsCheckingSession] = useState(true)
  const isSigningIn = inProgress !== 'none'

  useEffect(() => {
    const pendingAuthError = getAuthError()
    if (pendingAuthError) {
      setLoginError(pendingAuthError)
      clearAuthError()
    }
  }, [])

  // App.tsx already gates cached MSAL accounts before this page renders.
  // If we are here, show the fully branded page for a moment, then reveal
  // the explicit Microsoft sign-in button without starting any hidden auth flow.
  useEffect(() => {
    const timer = setTimeout(() => setIsCheckingSession(false), 2000)
    return () => clearTimeout(timer)
  }, [])

  const clearBrowserAuthState = async () => {
    try {
      await clearOrbitRuntimeCaches()
    } catch {}

    for (const account of instance.getAllAccounts()) {
      await instance.clearCache({ account }).catch(() => {})
    }

    instance.setActiveAccount(null)

    const sessionKeys = Object.keys(sessionStorage).filter(
      (key) => key.startsWith('msal.') || key.includes('msal') || key.includes('login') || key.includes('pkce')
    )
    sessionKeys.forEach((key) => sessionStorage.removeItem(key))

    const localKeys = Object.keys(localStorage).filter(
      (key) => key.startsWith('msal.') || key.includes('msal') || key.includes('login')
    )
    localKeys.forEach((key) => localStorage.removeItem(key))
  }

  const handleLogin = async () => {
    if (isSigningIn) return

    setLoginError(null)

    console.log('[Login] Starting loginRedirect')

    try {
      await instance.loginRedirect(buildLoginRedirectRequest())
    } catch (err: unknown) {
      const errObj = err as { errorCode?: string; errorMessage?: string; message?: string }
      const code = errObj.errorCode ?? 'unknown'
      const msg = errObj.errorMessage ?? errObj.message ?? String(err)
      console.error('[Login] loginRedirect failed:', code, msg, err)
      setLoginError(`${code}: ${msg}`)
    }
  }

  const handleResetAndRetry = async () => {
    if (isSigningIn || isResetting) return

    setLoginError(null)
    setIsResetting(true)

    try {
      await clearBrowserAuthState()
      await instance.loginRedirect(buildLoginRedirectRequest(undefined, 'login'))
    } catch (err: unknown) {
      const errObj = err as { errorCode?: string; errorMessage?: string; message?: string }
      const code = errObj.errorCode ?? 'unknown'
      const msg = errObj.errorMessage ?? errObj.message ?? String(err)
      console.error('[Login] reset-and-retry failed:', code, msg, err)
      setLoginError(`${code}: ${msg}`)
      setIsResetting(false)
    }
  }

  return (
    <div className="login-page min-h-screen flex flex-col">
      {/* ============ BACKGROUND LAYERS ============ */}
      <div className="login-bg" aria-hidden="true">
        <div className="login-bg-glow login-bg-glow--tl" />
        <div className="login-bg-glow login-bg-glow--br" />
        <div className="login-bg-glow login-bg-glow--center" />
        <div className="login-bg-grid" />
      </div>

      {/* ============ TOP BAR — animated orbit + wordmark ============ */}
      <header
        className="login-fade-in relative z-20 flex items-center gap-4 px-6 pt-6"
        style={{ animationDelay: '0.1s' }}
      >
        <OrbitBackdrop />
        <div className="flex flex-col leading-none">
          <span className="text-2xl font-bold text-white tracking-tight">
            Orbit
          </span>
          <span className="text-[11px] font-medium text-indigo-300/70 tracking-[0.25em] uppercase">
            ITSM
          </span>
          <span className="text-[11px] text-indigo-200/40 mt-1.5 tracking-wide">
            Modern IT Service Management.
          </span>
          <span className="text-[11px] text-indigo-200/40 mt-0.5 tracking-wide">
            Powered by Microsoft.
          </span>
        </div>
      </header>

      {/* ============ MAIN ============ */}
      <main className="relative z-20 flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-sm flex flex-col items-center text-center">

          {/* Login card with ICG logo */}
          <div
            className="login-fade-in login-card w-full flex flex-col items-center px-8 py-10"
            style={{ animationDelay: '0.2s' }}
          >
            {/* Main ICG group logo */}
            <img
              src="/main-logo.png"
              alt={branding.companyName}
              className="h-16 md:h-20 object-contain mb-6 brightness-[1.8] saturate-125"
            />

            {/* Portal title */}
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white leading-tight">
              IT Service Desk
            </h1>

            {/* Divider */}
            <div className="w-full border-t border-white/[0.08] my-6" />

            {/* Existing-session check */}
            {isCheckingSession && (
              <div className="w-full flex flex-col items-center gap-3 py-2">
                <div className="w-6 h-6 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin" />
                <p className="text-sm text-indigo-200/70">Signing you in…</p>
              </div>
            )}

            {/* Microsoft sign-in button */}
            {!isCheckingSession && (
              <button
                onClick={handleLogin}
                disabled={isSigningIn}
                className="login-btn group w-full flex items-center justify-center gap-3
                           py-3.5 px-6 rounded-2xl font-medium text-white text-[15px]
                           bg-white/[0.08] border border-white/[0.12]
                           hover:bg-white/[0.14] hover:border-white/[0.20]
                           shadow-lg shadow-black/20
                           transition-all duration-200 ease-out
                           disabled:opacity-70 disabled:cursor-not-allowed
                           focus:outline-none focus:ring-2 focus:ring-indigo-400/60 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                <MicrosoftLogo className="w-5 h-5" />
                {isSigningIn ? 'Signing in…' : 'Sign in with Microsoft'}
              </button>
            )}

            {/* Login error display */}
            {loginError && (
              <div className="mt-4 w-full rounded-xl bg-red-500/10 border border-red-400/20 px-4 py-3 text-left">
                <p className="text-xs font-semibold text-red-300 mb-1">Sign-in failed</p>
                <p className="text-[11px] text-red-200/80 break-all font-mono leading-relaxed">{loginError}</p>
                <button
                  onClick={handleResetAndRetry}
                  disabled={isSigningIn || isResetting}
                  className="mt-3 inline-flex items-center rounded-lg border border-red-300/20 px-3 py-2 text-[11px] font-medium text-red-100 hover:bg-red-400/10 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isResetting ? 'Resetting sign-in state…' : 'Reset sign-in state and choose account again'}
                </button>
              </div>
            )}

            {/* Support */}
            {branding.supportEmail && (
              <p className="text-xs text-indigo-300/30 mt-5">
                Need help?{' '}
                <a
                  href={`mailto:${branding.supportEmail}`}
                  className="text-indigo-400/60 hover:text-indigo-300 transition-colors underline underline-offset-2"
                >
                  Contact IT Support
                </a>
              </p>
            )}
          </div>

          {/* Subgroup logos */}
          <div
            className="login-fade-in mt-8 flex items-center justify-center gap-5 flex-wrap"
            style={{ animationDelay: '0.45s' }}
          >
            {[
              { src: '/ICGConstruction.png', alt: 'ICG Construction' },
              { src: '/ICGFlemming.png', alt: 'ICG Fleming' },
              { src: '/Steel.png', alt: 'Riverina Steel Frames' },
              { src: '/Plasta.png', alt: 'PlastaMasta Wagga Wagga' },
            ].map((logo) => (
              <img
                key={logo.alt}
                src={logo.src}
                alt={logo.alt}
                className="h-6 md:h-7 object-contain brightness-150 saturate-125"
              />
            ))}
          </div>
        </div>
      </main>

      {/* ============ FOOTER ============ */}
      <footer
        className="login-fade-in relative z-20 pb-5 text-center"
        style={{ animationDelay: '0.6s' }}
      >
        <p className="text-[10px] text-white/15 tracking-wide">
          {orbitPlatform.fullName} v{orbitPlatform.version} &middot; {orbitPlatform.copyright}
          <span className="ml-2 text-[9px] select-all">{buildTag}</span>
        </p>
        <p className="text-[10px] text-white/10 mt-1">
          Some usage statistics are logged in Orbit to continually improve service.
        </p>
      </footer>
    </div>
  )
}
